import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { Invoice } from 'api/Auth/getInvoicesForOrganization';
import { POLICY_SECTIONS } from './eulaText';

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        fontSize: 9,
        padding: 40,
        color: '#1a1a1a',
        lineHeight: 1.4,
    },
    // Header
    header: {
        borderBottomWidth: 2,
        borderBottomColor: '#0056b3',
        paddingBottom: 10,
        marginBottom: 16,
    },
    headerTitle: {
        fontSize: 18,
        fontFamily: 'Helvetica-Bold',
        color: '#0056b3',
        marginBottom: 2,
    },
    headerSubtitle: {
        fontSize: 9,
        color: '#666',
    },
    // Section
    sectionTitle: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        color: '#0056b3',
        marginBottom: 6,
        marginTop: 14,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomColor: '#dde3f0',
    },
    // Info grid
    infoRow: {
        flexDirection: 'row',
        marginBottom: 3,
    },
    infoLabel: {
        width: 130,
        fontFamily: 'Helvetica-Bold',
        color: '#444',
    },
    infoValue: {
        flex: 1,
        color: '#1a1a1a',
    },
    // Agreement box
    agreementBox: {
        backgroundColor: '#f0f4ff',
        borderLeftWidth: 3,
        borderLeftColor: '#0056b3',
        padding: 10,
        marginBottom: 8,
        borderRadius: 2,
    },
    agreementText: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: '#0056b3',
    },
    agreementSub: {
        fontSize: 8,
        color: '#555',
        marginTop: 2,
    },
    // EULA
    eulaSection: {
        marginBottom: 6,
    },
    eulaSectionTitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        color: '#333',
        marginBottom: 2,
    },
    eulaSectionBody: {
        fontSize: 7,
        color: '#444',
        lineHeight: 1.5,
    },
    // Table
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#0056b3',
        paddingVertical: 5,
        paddingHorizontal: 4,
        borderRadius: 2,
        marginBottom: 1,
    },
    tableHeaderCell: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 8,
        color: '#fff',
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 4,
        paddingHorizontal: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#eee',
    },
    tableRowAlt: {
        backgroundColor: '#f7f9fc',
    },
    tableCell: {
        fontSize: 8,
        color: '#1a1a1a',
    },
    tableCellPaid: {
        color: '#1a7a3c',
        fontFamily: 'Helvetica-Bold',
    },
    tableCellPastDue: {
        color: '#b91c1c',
        fontFamily: 'Helvetica-Bold',
    },
    tableCellOpen: {
        color: '#92400e',
        fontFamily: 'Helvetica-Bold',
    },
    // Summary row
    summaryRow: {
        flexDirection: 'row',
        backgroundColor: '#e8edf7',
        paddingVertical: 5,
        paddingHorizontal: 4,
        marginTop: 2,
        borderRadius: 2,
    },
    summaryLabel: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 8,
        color: '#0056b3',
    },
    summaryValue: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 8,
        color: '#1a1a1a',
    },
    // Status badge text
    noDataText: {
        fontSize: 9,
        color: '#888',
        fontStyle: 'italic',
        marginTop: 6,
    },
    // Footer
    footer: {
        position: 'absolute',
        bottom: 24,
        left: 40,
        right: 40,
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderTopWidth: 1,
        borderTopColor: '#dde3f0',
        paddingTop: 6,
    },
    footerText: {
        fontSize: 7,
        color: '#999',
    },
    pageNumber: {
        fontSize: 7,
        color: '#999',
    },
});

// ─── Column widths ─────────────────────────────────────────────────────────────

const COL = {
    num: '12%',
    date: '12%',
    period: '20%',
    reason: '16%',
    due: '10%',
    paid: '10%',
    remaining: '11%',
    status: '9%',
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(ts: number | null | undefined): string {
    if (!ts) return '--';
    return new Date(ts * 1000).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function formatCents(cents: number | null | undefined): string {
    if (cents == null) return '--';
    return `$${(cents / 100).toFixed(2)}`;
}

function statusStyle(status: string) {
    if (status === 'paid') return styles.tableCellPaid;
    if (status === 'past_due' || status === 'open') return styles.tableCellPastDue;
    return styles.tableCell;
}

function formatStatus(status: string): string {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Props ─────────────────────────────────────────────────────────────────────

export interface OrgReportData {
    entityType: 'organization';
    entityName: string;
    ownerName?: string | null;
    ownerEmail?: string | null;
    ownerPhone?: string | null;
    subscriptionTier?: string | null;
    subscriptionStatus?: string | null;
    stripeCustomerId?: string | null;
    agreementDate: string;
    reportGeneratedAt: string;
    invoices: Invoice[];
    invoicesFetchFailed?: boolean;
}

export interface AthleteReportData {
    entityType: 'athlete';
    entityName: string;
    serialNumber?: string | null;
    organizationName?: string | null;
    subscriptionStatus?: string | null;
    subscriptionStartedAt?: string | null;
    assignmentType?: string;
    agreementDate: string;
    reportGeneratedAt: string;
}

type ReportData = OrgReportData | AthleteReportData;

// ─── Sub-components ────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <View style={styles.infoRow}>
        <Text style={styles.infoLabel}>{label}:</Text>
        <Text style={styles.infoValue}>{value}</Text>
    </View>
);

const PolicyContent: React.FC = () => (
    <View>
        {POLICY_SECTIONS.map((section) => (
            <View key={section.title} style={styles.eulaSection}>
                <Text style={styles.eulaSectionTitle}>{section.title}</Text>
                <Text style={styles.eulaSectionBody}>{section.body}</Text>
            </View>
        ))}
    </View>
);

const InvoiceTable: React.FC<{ invoices: Invoice[]; fetchFailed?: boolean }> = ({
    invoices,
    fetchFailed,
}) => {
    if (fetchFailed) {
        return (
            <Text style={styles.noDataText}>
                Payment history could not be retrieved automatically for admin users. Please view
                the full invoice history directly in the Stripe dashboard using the Stripe Sub link
                in the admin panel.
            </Text>
        );
    }
    if (!invoices.length) {
        return <Text style={styles.noDataText}>No invoice records found.</Text>;
    }

    const totalDue = invoices.reduce((s, inv) => s + (inv.amount_due ?? 0), 0);
    const totalPaid = invoices.reduce((s, inv) => s + (inv.amount_paid ?? 0), 0);
    const totalRemaining = invoices.reduce((s, inv) => s + (inv.amount_remaining ?? 0), 0);
    const pastDueCount = invoices.filter((inv) => inv.status === 'past_due').length;
    const lateCount = invoices.filter(
        (inv) => inv.status === 'paid' && inv.due_date && inv.created > inv.due_date,
    ).length;

    return (
        <View>
            {/* Summary stats */}
            <View style={{ flexDirection: 'row', gap: 12, marginBottom: 8 }}>
                <View style={styles.agreementBox}>
                    <Text style={styles.agreementText}>{invoices.length}</Text>
                    <Text style={styles.agreementSub}>Total Invoices</Text>
                </View>
                <View style={styles.agreementBox}>
                    <Text style={styles.agreementText}>
                        {invoices.filter((i) => i.status === 'paid').length}
                    </Text>
                    <Text style={styles.agreementSub}>Paid</Text>
                </View>
                <View
                    style={[
                        styles.agreementBox,
                        pastDueCount > 0
                            ? { backgroundColor: '#fff0f0', borderLeftColor: '#b91c1c' }
                            : {},
                    ]}
                >
                    <Text
                        style={[styles.agreementText, pastDueCount > 0 ? { color: '#b91c1c' } : {}]}
                    >
                        {pastDueCount}
                    </Text>
                    <Text style={styles.agreementSub}>Past Due</Text>
                </View>
                {lateCount > 0 && (
                    <View
                        style={[
                            styles.agreementBox,
                            { backgroundColor: '#fffbea', borderLeftColor: '#d97706' },
                        ]}
                    >
                        <Text style={[styles.agreementText, { color: '#d97706' }]}>
                            {lateCount}
                        </Text>
                        <Text style={styles.agreementSub}>Paid Late</Text>
                    </View>
                )}
            </View>

            {/* Table */}
            <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderCell, { width: COL.num }]}>Invoice #</Text>
                <Text style={[styles.tableHeaderCell, { width: COL.date }]}>Date</Text>
                <Text style={[styles.tableHeaderCell, { width: COL.period }]}>Period</Text>
                <Text style={[styles.tableHeaderCell, { width: COL.reason }]}>Reason</Text>
                <Text style={[styles.tableHeaderCell, { width: COL.due }]}>Due</Text>
                <Text style={[styles.tableHeaderCell, { width: COL.paid }]}>Paid</Text>
                <Text style={[styles.tableHeaderCell, { width: COL.remaining }]}>Remaining</Text>
                <Text style={[styles.tableHeaderCell, { width: COL.status }]}>Status</Text>
            </View>

            {invoices.map((inv, idx) => (
                <View
                    key={inv.id}
                    style={[styles.tableRow, idx % 2 === 1 ? styles.tableRowAlt : {}]}
                    wrap={false}
                >
                    <Text style={[styles.tableCell, { width: COL.num }]}>{inv.number || '--'}</Text>
                    <Text style={[styles.tableCell, { width: COL.date }]}>
                        {formatDate(inv.created)}
                    </Text>
                    <Text style={[styles.tableCell, { width: COL.period }]}>
                        {formatDate(inv.period_start)} – {formatDate(inv.period_end)}
                    </Text>
                    <Text style={[styles.tableCell, { width: COL.reason }]}>
                        {(inv.billing_reason || '').replace(/_/g, ' ')}
                    </Text>
                    <Text style={[styles.tableCell, { width: COL.due }]}>
                        {formatCents(inv.amount_due)}
                    </Text>
                    <Text style={[styles.tableCell, { width: COL.paid }]}>
                        {formatCents(inv.amount_paid)}
                    </Text>
                    <Text style={[styles.tableCell, { width: COL.remaining }]}>
                        {formatCents(inv.amount_remaining)}
                    </Text>
                    <Text
                        style={[styles.tableCell, statusStyle(inv.status), { width: COL.status }]}
                    >
                        {formatStatus(inv.status)}
                    </Text>
                </View>
            ))}

            {/* Totals */}
            <View style={styles.summaryRow}>
                <Text style={[styles.summaryLabel, { width: COL.num }]}>Totals</Text>
                <Text style={{ width: COL.date }}></Text>
                <Text style={{ width: COL.period }}></Text>
                <Text style={{ width: COL.reason }}></Text>
                <Text style={[styles.summaryValue, { width: COL.due }]}>
                    {formatCents(totalDue)}
                </Text>
                <Text style={[styles.summaryValue, { width: COL.paid }]}>
                    {formatCents(totalPaid)}
                </Text>
                <Text style={[styles.summaryValue, { width: COL.remaining }]}>
                    {formatCents(totalRemaining)}
                </Text>
                <Text style={{ width: COL.status }}></Text>
            </View>
        </View>
    );
};

// ─── Main Document ─────────────────────────────────────────────────────────────

const SubscriptionReportPDF: React.FC<{ data: ReportData }> = ({ data }) => {
    const isOrg = data.entityType === 'organization';

    return (
        <Document
            title={`Subscription Report – ${data.entityName}`}
            author="Athlete Management Technologies, LLC."
            creator="1TUL Admin"
        >
            {/* ── Page 1: Customer Info + Agreement ── */}
            <Page size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.headerTitle}>Subscription Payment Report</Text>
                    <Text style={styles.headerSubtitle}>
                        Athlete Management Technologies, LLC. · 1TUL Platform
                    </Text>
                    <Text style={styles.headerSubtitle}>
                        Report Generated: {data.reportGeneratedAt}
                    </Text>
                </View>

                {/* Customer info */}
                <Text style={styles.sectionTitle}>
                    {isOrg ? 'Organization Information' : 'Athlete Information'}
                </Text>

                {isOrg ? (
                    <View>
                        <InfoRow
                            label="Organization Name"
                            value={(data as OrgReportData).entityName}
                        />
                        {(data as OrgReportData).ownerName && (
                            <InfoRow
                                label="Owner / Contact"
                                value={(data as OrgReportData).ownerName!}
                            />
                        )}
                        {(data as OrgReportData).ownerEmail && (
                            <InfoRow label="Email" value={(data as OrgReportData).ownerEmail!} />
                        )}
                        {(data as OrgReportData).ownerPhone && (
                            <InfoRow label="Phone" value={(data as OrgReportData).ownerPhone!} />
                        )}
                        {(data as OrgReportData).subscriptionTier && (
                            <InfoRow
                                label="Subscription Tier"
                                value={(data as OrgReportData).subscriptionTier!}
                            />
                        )}
                        {(data as OrgReportData).subscriptionStatus && (
                            <InfoRow
                                label="Subscription Status"
                                value={formatStatus((data as OrgReportData).subscriptionStatus!)}
                            />
                        )}
                        {(data as OrgReportData).stripeCustomerId && (
                            <InfoRow
                                label="Stripe Customer ID"
                                value={(data as OrgReportData).stripeCustomerId!}
                            />
                        )}
                    </View>
                ) : (
                    <View>
                        <InfoRow
                            label="Athlete Name"
                            value={(data as AthleteReportData).entityName}
                        />
                        {(data as AthleteReportData).serialNumber && (
                            <InfoRow
                                label="Serial Number"
                                value={(data as AthleteReportData).serialNumber!}
                            />
                        )}
                        {(data as AthleteReportData).organizationName && (
                            <InfoRow
                                label="Organization"
                                value={(data as AthleteReportData).organizationName!}
                            />
                        )}
                        {(data as AthleteReportData).assignmentType && (
                            <InfoRow
                                label="Assignment Type"
                                value={(data as AthleteReportData).assignmentType!}
                            />
                        )}
                        {(data as AthleteReportData).subscriptionStatus && (
                            <InfoRow
                                label="Subscription Status"
                                value={formatStatus(
                                    (data as AthleteReportData).subscriptionStatus!,
                                )}
                            />
                        )}
                    </View>
                )}

                {/* Agreement timestamp */}
                <Text style={styles.sectionTitle}>Terms Agreement</Text>
                <View style={styles.agreementBox}>
                    <Text style={styles.agreementText}>
                        Billing & Return Policy Agreed On: {data.agreementDate}
                    </Text>
                    <Text style={styles.agreementSub}>
                        By activating a subscription and placing an order with the 1TUL / FiyrPod
                        platform, the account holder acknowledged and agreed to the Billing & Return
                        Policy of Athlete Management Technologies, LLC. on the date shown above.
                        Full policy text is included on the following page.
                    </Text>
                </View>

                {/* Payment history for orgs */}
                {isOrg && (
                    <>
                        <Text style={styles.sectionTitle}>Payment History</Text>
                        <InvoiceTable
                            invoices={(data as OrgReportData).invoices}
                            fetchFailed={(data as OrgReportData).invoicesFetchFailed}
                        />
                    </>
                )}

                {/* For athletes: show subscription data note */}
                {!isOrg && (
                    <>
                        <Text style={styles.sectionTitle}>Subscription Details</Text>
                        {(data as AthleteReportData).subscriptionStartedAt ? (
                            <InfoRow
                                label="Subscription Start"
                                value={(data as AthleteReportData).subscriptionStartedAt!}
                            />
                        ) : null}
                        <Text style={[styles.noDataText, { marginTop: 4 }]}>
                            Detailed invoice history for individual athlete subscriptions is
                            available directly in the Stripe dashboard via the Stripe Sub link in
                            the admin panel.
                        </Text>
                    </>
                )}

                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>
                        Athlete Management Technologies, LLC. · support@1tul.com · Confidential
                    </Text>
                    <Text
                        style={styles.pageNumber}
                        render={({ pageNumber, totalPages }) =>
                            `Page ${pageNumber} of ${totalPages}`
                        }
                    />
                </View>
            </Page>

            {/* ── Page 2+: Billing & Return Policy ── */}
            <Page size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.headerTitle}>FiyrPod Billing & Return Policy</Text>
                    <Text style={styles.headerSubtitle}>
                        Athlete Management Technologies, LLC. · Full Policy Text
                    </Text>
                </View>

                <PolicyContent />

                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>
                        Athlete Management Technologies, LLC. · support@1tul.com · Confidential
                    </Text>
                    <Text
                        style={styles.pageNumber}
                        render={({ pageNumber, totalPages }) =>
                            `Page ${pageNumber} of ${totalPages}`
                        }
                    />
                </View>
            </Page>
        </Document>
    );
};

export default SubscriptionReportPDF;
